import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Eye, EyeOff, X } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import CountrySelect, { COUNTRIES } from './CountrySelect';
import ForgotPasswordModal from './ForgotPasswordModal';

function WhatsAppIcon({ size = 18 }) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} className="fill-current">
      <path d="M19.11 17.205c-.372 0-.892 1.044-1.353.974-.428-.075-.901-.27-1.327-.555-1.13-.747-1.916-1.477-2.6-2.6-.285-.426-.48-.9-.554-1.327-.07-.46.974-.981.974-1.353 0-.371-.974-1.59-1.299-1.946-.279-.305-.587-.34-.722-.341-.186 0-.4-.046-.611-.046-.211 0-.55.082-.835.387-.286.305-1.092 1.066-1.092 2.604 0 1.537 1.118 3.022 1.273 3.23.155.21 2.198 3.357 5.327 4.706.745.32 1.327.512 1.781.656.748.237 1.43.204 1.969.124.6-.09 1.846-.755 2.107-1.486.26-.73.26-1.358.182-1.486-.077-.128-.286-.205-.6-.36-.313-.155-1.847-.91-2.133-1.014-.286-.103-.494-.155-.703.155-.21.31-.808 1.014-.99 1.224-.181.21-.363.236-.677.082zM16 4C9.373 4 4 9.373 4 16c0 2.244.611 4.345 1.676 6.149L4 28l5.998-1.654A11.94 11.94 0 0016 28c6.627 0 12-5.373 12-12S22.627 4 16 4z" />
    </svg>
  );
}

export default function LoginModal({ open, onOpenChange, onSwitchToSignup }) {
  const { data } = useSite();
  const { login } = useAuth();
  const m = data.loginModal || {};
  const [showPwd, setShowPwd] = useState(false);
  const [country, setCountry] = useState(COUNTRIES[0]);
  const [mobile, setMobile] = useState('');
  const [pwd, setPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mobile || mobile.length < 6) return toast.error('Enter a valid mobile number');
    if (!pwd) return toast.error('Please enter password');
    setLoading(true);
    try {
      const fullMobile = country.code.replace('+', '') + mobile;
      const u = await login(fullMobile, pwd);
      toast.success(`Welcome back, ${u.name?.split(' ')[0] || 'User'}`);
      onOpenChange(false);
      setMobile(''); setPwd('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] bg-[#1a1a1a] border border-zinc-800 text-zinc-100 p-0 sm:rounded-xl [&>button]:hidden">
        <DialogTitle className="sr-only">Login</DialogTitle>
        <DialogDescription className="sr-only">Sign in to your account</DialogDescription>
        <span className="absolute right-3 top-3 z-20">
          <button onClick={() => onOpenChange(false)} className="w-8 h-8 rounded-full text-zinc-300 hover:text-white bg-black/30 hover:bg-red-500/80 border border-white/10 flex items-center justify-center transition" data-testid="login-close-btn" aria-label="Close login">
            <X size={18} />
          </button>
        </span>

        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h2 className="text-2xl font-bold text-white">{m.title || 'Login'}</h2>
          <div className="flex items-center gap-1.5 mr-6">
            {data.branding.logoImage && (
              <img src={data.branding.logoImage} alt="logo" className="h-7 w-auto object-contain" />
            )}
            <div className="flex items-baseline gap-0.5">
              <span className="font-display text-xl font-extrabold tracking-[0.12em] text-[color:var(--gold)]">{data.branding.logoText}</span>
              <span className="font-display text-[9px] font-bold text-[color:var(--gold)]/80">{data.branding.logoAccent}</span>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-[13px] font-semibold text-zinc-200 mb-1.5 block">{m.mobileLabel || 'Mobile Number'}</label>
              <div className="flex items-stretch bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden focus-within:border-[color:var(--gold)]/50">
                <CountrySelect value={country} onChange={setCountry} />
                <input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 15))} placeholder="Enter Mobile Number" className="flex-1 h-11 bg-transparent px-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none" />
              </div>
            </div>

            <div>
              <label className="text-[13px] font-semibold text-zinc-200 mb-1.5 block">{m.passwordLabel || 'Password'}</label>
              <div className="relative">
                <Input type={showPwd ? 'text' : 'password'} value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder={m.passwordPlaceholder} className="h-11 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 pr-10" />
                <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                  {showPwd ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
              <div className="text-right mt-2">
                <button type="button" onClick={() => { onOpenChange(false); setForgotOpen(true); }} className="text-[color:var(--gold)] text-[13px] font-bold underline" data-testid="login-forgot-btn">{m.forgotLabel || 'Forgot Password?'}</button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="w-full h-11 rounded-md bg-[color:var(--gold)] hover:brightness-110 disabled:opacity-60 text-black font-bold text-[15px] click-ripple transition" data-testid="login-submit-btn">
              {loading ? 'Logging in...' : (m.loginBtn || 'Login')}
            </button>

            <div className="flex items-center gap-3 text-xs text-zinc-500 py-1">
              <div className="flex-1 h-px bg-zinc-800" />{m.orText || 'OR'}<div className="flex-1 h-px bg-zinc-800" />
            </div>

            <a href={data.whatsappLink} target="_blank" rel="noreferrer" className="w-full h-11 rounded-md bg-[#25d366] hover:bg-[#1ebd5d] text-white font-bold text-[15px] flex items-center justify-center gap-2 transition">
              <WhatsAppIcon size={18} /> {m.whatsappBtn}
            </a>
          </form>

          <div className="text-center mt-4 text-[13px] text-zinc-400">
            Don&apos;t have an account?{' '}
            <button onClick={onSwitchToSignup} className="text-[color:var(--gold)] font-bold hover:underline">Sign up</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <ForgotPasswordModal open={forgotOpen} onOpenChange={setForgotOpen} />
    </>
  );
}
